import { gsap } from 'gsap';
import React, { createRef, useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useDidMount } from '../helpers/useDidMount';
const SegmentedControlWrapper = styled.div `
  display: inline-flex;
  vertical-align: top;
  box-sizing: border-box;
  gap: 24px;
  max-height: max-content;

  cursor: ${({ isDisabled }) => isDisabled && 'not-allowed'};
  opacity: ${({ isDisabled }) => isDisabled && 0.5};

  width: ${({ fullWidth, fullWidthEqual }) => (fullWidth || fullWidthEqual ? '100%' : 'min-content')};

  ${({ line }) => !line &&
    css `
      gap: ${({ theme }) => theme.style.segmentedControlGap};
      padding: ${({ theme }) => theme.style.segmentedControlPadding};
      border-radius: ${({ theme }) => theme.style.segmentedControlBorderRadius};
      background-color: ${({ theme }) => theme.style.segmentedControlBackgroundColor};
      box-shadow:
        ${({ theme }) => theme.style.segmentedControlBorderProperties}
          ${({ theme }) => theme.style.segmentedControlBorderWidth}
          ${({ theme }) => theme.style.segmentedControlBorderColor},
        ${({ theme }) => theme.style.segmentedControlShadow};
    `}

  ${({ line }) => line &&
    css `
      position: relative;
      border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};

      &:before {
        content: '';
        height: 2px;
        background-color: var(
          --option-active-background,
          --option-background,
          ${({ theme }) => theme.primary.background}
        );
        position: absolute;
        left: var(--segmentedcontrol-active-left, 0);
        width: var(--segmentedcontrol-active-width, 0);
        top: calc(100% - 1px);
        box-shadow: 0px 0px 16px ${({ theme }) => theme.primary.blendToBackground(1000, 200)};
        transition: left 0.1s ease-out;
      }
    `}

  ${({ size }) => size === 'small' &&
    css `
      gap: 16px;

      ${({ line }) => !line &&
        css `
          gap: ${({ theme }) => theme.style.segmentedControlSmallGap};
          padding: ${({ theme }) => theme.style.segmentedControlSmallPadding};
          border-radius: ${({ theme }) => theme.style.segmentedControlSmallBorderRadius};
        `}
    `}

  ${({ size }) => size === 'tiny' &&
    css `
      gap: 12px;

      ${({ line }) => !line &&
        css `
          gap: ${({ theme }) => theme.style.segmentedControlTinyGap};
          padding: ${({ theme }) => theme.style.segmentedControlTinyPadding};
          border-radius: ${({ theme }) => theme.style.segmentedControlTinyBorderRadius};
        `}
    `}
`;
export const SegmentedControlOption = styled.button `
  appearance: none;
  outline: none;
  border: none;
  background: none;
  display: flex;
  white-space: nowrap;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-family: inherit;
  font-size: ${({ theme }) => theme.style.formElementFontSizeM};
  font-weight: 500;
  cursor: pointer;
  padding: 7px 16px;
  line-height: 20px;
  color: var(--option-color, ${({ theme }) => theme.style.segmentedControlOptionColor});
  transition: color 0.1s;
  cursor: ${({ isDisabled }) => isDisabled && 'not-allowed'};

  flex-grow: ${({ fullWidth, fullWidthEqual }) => (fullWidth || fullWidthEqual) && '1'};
  justify-content: ${({ fullWidth, fullWidthEqual }) => (fullWidth || fullWidthEqual) && 'center'};

  flex-basis: ${({ fullWidthEqual }) => fullWidthEqual && '0'};

  ${({ line }) => !line &&
    css `
      border-radius: ${({ theme }) => theme.style.segmentedControlOptionBorderRadius};
      background-color: var(--option-background, transparent);
      transition:
        color 0.2s,
        background-color 0.2s,
        box-shadow var(--box-shadow-duration, 0.2s),
        transform 0.2s;
      transform: scale(var(--option-scale, 1)) translateZ(0);

      &:hover {
        --option-background: ${({ theme }) => theme.style.segmentedControlOptionHoveredBackgroundColor};
        --option-color: ${({ theme }) => theme.style.segmentedControlOptionHoveredColor};
      }
      &:active {
        --option-scale: 0.975;
      }

      ${({ isActive }) => isActive &&
        css `
          --option-active-color: ${({ theme }) => theme.style.segmentedControlOptionActiveColor};
          --option-active-background: ${({ theme }) => theme.style.segmentedControlOptionActiveBackgroundColor};
          --box-shadow-duration: 0.35s;

          color: var(--option-active-color, ${({ theme }) => theme.style.segmentedControlOptionColor});
          background-color: var(--option-active-background, transparent);
          box-shadow: ${({ theme }) => theme.style.segmentedControlOptionActiveShadow};

          &:hover {
            --option-active-background: ${({ theme }) => theme.style.segmentedControlOptionActiveBackgroundColor};
            --option-active-color: ${({ theme }) => theme.style.segmentedControlOptionActiveColor};
          }
        `}

      ${({ isActive }) => !isActive &&
        css `
          &:hover {
            --option-background: ${({ theme }) => theme.style.segmentedControlOptionHoveredBackgroundColor};
          }
        `}
    `}

  ${({ line }) => line &&
    css `
      padding: 0 0 12px 0;
      --option-color: ${({ theme }) => theme.secondary.background};

      ${({ isActive }) => isActive &&
        css `
          --option-active-color: ${({ theme }) => theme.blue.background};

          color: var(--option-active-color, ${({ theme }) => theme.style.segmentedControlOptionColor});
          background-color: var(--option-active-background, transparent);
        `}

      ${({ isActive }) => !isActive &&
        css `
          &:hover {
            --option-color: ${({ theme }) => theme.secondary.background};
          }
        `}
    `}

  svg {
    --icon-size: 20px;
    --icon-color: currentColor;
    margin: 0 -4px;

    ${({ withIcon }) => withIcon &&
    css `
        margin: 0 0 0 -8px;
        opacity: 0.8;
      `}
  }

  ${({ size }) => size === 'small' &&
    css `
      padding: ${({ theme }) => theme.style.segmentedControlSmallOptionPadding};

      ${({ line }) => line &&
        css `
          padding: 0 0 8px 0;
        `}
    `}

  ${({ size }) => size === 'tiny' &&
    css `
      padding: ${({ theme }) => theme.style.segmentedControlTinyOptionPadding}};
      font-size: ${({ theme }) => theme.style.segmentedControlTinyOptionFontSize};
      font-weight: ${({ theme }) => theme.style.segmentedControlTinyOptionFontWeight};
      border-radius: ${({ theme }) => theme.style.segmentedControlTinyOptionBorderRadius};

      ${({ line }) => line &&
        css `
          padding: 0 0 6px 0;
        `}
    `}
`;
export const SegmentedControl = (props) => {
    const wrapperRef = createRef();
    const didMount = useDidMount();
    const styleProps = {
        size: props.size ?? 'default',
        line: props.line ?? false,
        withIcon: props.withIcon ?? false,
        isDisabled: props.isDisabled ?? false,
        fullWidth: props.fullWidth ?? false,
        fullWidthEqual: props.fullWidthEqual ?? false,
    };
    const isActive = useCallback((value, active, removeWildcard) => {
        if (typeof active !== 'undefined' || props.activeUndefined) {
            if (props.activeWildcard && typeof active === 'string' && typeof value === 'string' && !removeWildcard) {
                return active.startsWith(value) || value === active;
            }
            return Array.isArray(value) && active ? value.includes(active.toString()) : value === active;
        }
        return false;
    }, [props.activeWildcard, props.activeUndefined]);
    const handleClick = useCallback((option) => {
        if (props.isDisabled) {
            return;
        }
        if (props.onOptionChange) {
            props.onOptionChange(Array.isArray(option.value) ? option.value[0] : option.value);
        }
        if (option.onClick) {
            option.onClick(Array.isArray(option.value) ? option.value[0] : option.value);
        }
    }, [props.isDisabled, props.onOptionChange]);
    const updateActiveLinePosition = useCallback((duration = 0.3) => {
        const wrapper = wrapperRef.current;
        if (!wrapper || !props.line) {
            return;
        }
        const current = props.options.findIndex((e) => isActive(e.value, props.active, e.removeWildcard));
        const activeElement = wrapper.children.item(current);
        if (activeElement) {
            const left = activeElement.offsetLeft;
            const width = activeElement.offsetWidth;
            gsap.to(wrapper, {
                '--segmentedcontrol-active-left': `${left}px`,
                '--segmentedcontrol-active-width': `${width}px`,
                duration,
                ease: 'none',
            });
        }
    }, [props.active, props.options, props.line, wrapperRef, isActive]);
    useEffect(() => {
        if (props.line) {
            window.addEventListener('resize', () => updateActiveLinePosition(0));
            document.addEventListener('DOMContentLoaded', () => updateActiveLinePosition(0));
            document.fonts.ready.then(() => updateActiveLinePosition(0));
            return () => {
                window.removeEventListener('resize', () => updateActiveLinePosition(0));
                document.removeEventListener('DOMContentLoaded', () => updateActiveLinePosition(0));
            };
        }
        return;
    }, [props.line]);
    useEffect(() => {
        if (didMount && props.line) {
            updateActiveLinePosition(0.3);
        }
    }, [props.active, didMount, updateActiveLinePosition, props.line]);
    return (React.createElement(SegmentedControlWrapper, { className: props.className, ref: wrapperRef, ...styleProps }, props.options.map((option, index) => (React.createElement(SegmentedControlOption, { onClick: () => handleClick(option), isActive: isActive(option.value, props.active, option.removeWildcard), key: index, type: "button", ...styleProps }, option.label)))));
};
//# sourceMappingURL=SegmentedControl.js.map